import { settingApi } from '../../../slice/settingApi';

const BILLING_API = {
  // Subscriptions
  getSubscriptionList: 'api/billing/subscriptions',
  createSubscription: 'api/billing/subscriptions',
  buySubscription: 'api/billing/subscriptions/change',
  upgradeSubscription: 'api/billing/subscriptions/upgrade',
  downgradeSubscription: 'api/billing/subscriptions/downgrade',
  cancelSubscription: 'api/billing/subscriptions/cancel',
  reactivateSubscription: 'api/billing/subscriptions/reactivate',
  changeSubscriptionPlanQuantity: 'api/billing/subscription/quantity',
  planChange: 'api/billing/subscription/plan-change',
  compareSubscription: 'api/billing/subscription/compare',
  expireSubscriptions: 'api/billing/subscriptions/expire',
  renewSubscription: 'api/billing/subscriptions/renew',
  renewAddonSubscription: 'api/billing/subscriptions/addon/renew',

  // Checkout & Portal
  checkout: 'api/billing/checkout',
  portal: 'api/billing/portal',

  // Plans & Add-Ons
  getPlanList: 'api/billing/plans',
  getAddonsList: 'api/billing/addons',
  buyAddons: 'api/billing/addons/buy',
  addonSubscriptions: 'api/billing/addons/subscriptions',

  // Payments & Balance
  processPayment: 'api/billing/payment/process',
  getBalance: 'api/billing/balance',
  addBalance: 'api/billing/balance/add',
  getTransactionList: 'api/billing/transactions',

  // Invoices
  invoices: 'api/billing/invoices',
  invoiceList: 'api/billing/invoices/list',
  invoiceDetail: 'api/billing/invoices/details',
  markInvoiceAsPaid: 'api/billing/invoices/mark-paid',

  // Discounts & Coupons
  discountAdd: 'api/billing/discounts/add',
  discountList: 'api/billing/discounts/list',
  discountUpdate: 'api/billing/discounts/update',
  discountDetails: 'api/billing/discounts/details',
  discountDelete: 'api/billing/discounts/delete',
  couponList: 'api/billing/coupons/list',
  removeAllCoupons: 'api/billing/coupons/remove-all',

  // Estimates & Quotes
  estimate: 'api/billing/estimate/items',
  chargeEstimate: 'api/billing/estimate/charge',
  createQuote: 'api/billing/quote',
  convertQuote: 'api/billing/quote/convert',

  // DIDs
  searchDids: 'api/billing/dids/search',
  buyDid: 'api/billing/dids/buy',

  // Usage & Monitoring
  getUsage: 'api/billing/usage',
  usageThresholds: 'api/billing/usage/thresholds',
  cleanup: 'api/billing/cleanup',

  // Moyasar Payment Gateway
  moyasarGenerateToken: 'api/billing/moyasar/generate-token',
  moyasarCreatePayment: 'api/billing/moyasar/create-payment',
  moyasarCreateSubscription: 'api/billing/moyasar/create-subscription',
  moyasarRegisterWebhook: 'api/billing/moyasar/register-webhook',

  // Admin APIs
  registerOnChargebee: 'api/billing/register',
  manualCharge: 'api/billing/charge/manual',
  itemFamilies: 'api/billing/item-families',
  jerasoftProxy: 'api/billing/jerasoft',
};

export const billingApi = settingApi.injectEndpoints({
  endpoints: (builder) => ({
    // ─── Subscriptions ───────────────────────────────────────────────────
    getSubscriptionList: builder.mutation({
      query: (filter) => ({
        url: BILLING_API.getSubscriptionList,
        method: 'POST',
        body: filter,
      }),
    }),
    createSubscription: builder.mutation({
      query: (data) => ({
        url: BILLING_API.createSubscription,
        method: 'POST',
        body: data,
      }),
    }),
    buySubscription: builder.mutation({
      query: (data) => ({
        url: BILLING_API.buySubscription,
        method: 'PUT',
        body: data,
      }),
    }),
    cancelSubscription: builder.mutation({
      query: (data) => ({
        url: BILLING_API.cancelSubscription,
        method: 'DELETE',
        body: data,
      }),
    }),
    reactivateSubscription: builder.mutation({
      query: (data) => ({
        url: BILLING_API.reactivateSubscription,
        method: 'PUT',
        body: data,
      }),
    }),
    changeSubscriptionPlanQuantity: builder.mutation({
      query: (data) => ({
        url: BILLING_API.changeSubscriptionPlanQuantity,
        method: 'POST',
        body: typeof data === 'number' ? { quantity: data } : data,
      }),
    }),
    getCompareSubscriptionChange: builder.mutation({
      query: (filter) => ({
        url: BILLING_API.compareSubscription,
        method: 'POST',
        body: filter,
      }),
    }),
    renewSubscription: builder.mutation({
      query: (data) => ({
        url: BILLING_API.renewSubscription,
        method: 'PUT',
        body: data,
      }),
    }),
    renewAddonSubscription: builder.mutation({
      query: (data) => ({
        url: BILLING_API.renewAddonSubscription,
        method: 'PUT',
        body: data,
      }),
    }),
    expireSubscriptions: builder.mutation({
      query: (data) => ({
        url: BILLING_API.expireSubscriptions,
        method: 'PUT',
        body: data,
      }),
    }),

    // ─── Checkout & Portal ───────────────────────────────────────────────
    createCheckout: builder.mutation({
      query: (data) => ({
        url: BILLING_API.checkout,
        method: 'POST',
        body: data,
      }),
    }),
    portal: builder.mutation({
      query: (data) => ({
        url: BILLING_API.portal,
        method: 'POST',
        body: data,
      }),
    }),

    // ─── Plans & Add-Ons ─────────────────────────────────────────────────
    getPlanList: builder.mutation({
      query: (filter) => ({
        url: BILLING_API.getPlanList,
        method: 'POST',
        body: filter,
      }),
    }),
    getAddonsList: builder.mutation({
      query: (filter) => {
        const { tenantId, ...params } = filter || {};
        const headers: Record<string, string> = {};
        if (tenantId) {
          headers['x-tenant-id'] = tenantId;
          headers.tenantId = tenantId;
        }
        return {
          url: BILLING_API.getAddonsList,
          method: 'GET',
          params,
          headers,
        };
      },
    }),
    buyAddons: builder.mutation({
      query: (data) => ({
        url: BILLING_API.buyAddons,
        method: 'POST',
        body: data,
      }),
    }),
    getAddonSubscriptions: builder.mutation({
      query: (filter) => ({
        url: BILLING_API.addonSubscriptions,
        method: 'POST',
        body: filter,
      }),
    }),

    // ─── Payments & Balance ──────────────────────────────────────────────
    processPayment: builder.mutation({
      query: (data) => ({
        url: BILLING_API.processPayment,
        method: 'POST',
        body: data,
      }),
    }),
    getLowBalance: builder.mutation({
      query: (filter) => ({
        url: BILLING_API.getBalance,
        method: 'GET',
      }),
    }),
    addBalance: builder.mutation({
      query: (data) => ({
        url: BILLING_API.addBalance,
        method: 'POST',
        body: data,
      }),
    }),
    getTransactionList: builder.mutation({
      query: (filter) => ({
        url: BILLING_API.getTransactionList,
        method: 'POST',
        body: filter,
      }),
    }),

    // ─── Invoices ────────────────────────────────────────────────────────
    invoiceList: builder.mutation({
      query: (invoiceQuery) => ({
        url: BILLING_API.invoiceList,
        method: 'POST',
        body: invoiceQuery,
      }),
    }),
    invoiceDetail: builder.mutation({
      query: (data) => ({
        url: BILLING_API.invoiceDetail,
        method: 'POST',
        body: data,
      }),
    }),
    markInvoiceAsPaid: builder.mutation({
      query: (data) => ({
        url: BILLING_API.markInvoiceAsPaid,
        method: 'POST',
        body: data,
      }),
    }),

    // ─── Discounts & Coupons ─────────────────────────────────────────────
    discountAdd: builder.mutation({
      query: (data) => ({
        url: BILLING_API.discountAdd,
        method: 'POST',
        body: data,
      }),
    }),
    discountUpdate: builder.mutation({
      query: (data) => ({
        url: BILLING_API.discountUpdate,
        method: 'POST',
        body: data,
      }),
    }),
    discountDetails: builder.mutation({
      query: (data) => ({
        url: BILLING_API.discountDetails,
        method: 'POST',
        body: data,
      }),
    }),
    discountList: builder.mutation({
      query: (filter) => ({
        url: BILLING_API.discountList,
        method: 'POST',
        body: filter,
      }),
    }),
    discountDelete: builder.mutation({
      query: (ids) => ({
        url: BILLING_API.discountDelete,
        method: 'POST',
        body: { ids },
      }),
    }),
    couponList: builder.mutation({
      query: (filter) => ({
        url: BILLING_API.couponList,
        method: 'POST',
        body: filter,
      }),
    }),
    removeAllCoupons: builder.mutation({
      query: (data) => ({
        url: BILLING_API.removeAllCoupons,
        method: 'DELETE',
        body: data,
      }),
    }),

    // ─── Estimates & Quotes ──────────────────────────────────────────────
    estimate: builder.mutation({
      query: (data) => ({
        url: BILLING_API.estimate,
        method: 'POST',
        body: data,
      }),
    }),
    chargeEstimate: builder.mutation({
      query: (data) => ({
        url: BILLING_API.chargeEstimate,
        method: 'POST',
        body: data,
      }),
    }),
    createQuote: builder.mutation({
      query: (data) => ({
        url: BILLING_API.createQuote,
        method: 'POST',
        body: data,
      }),
    }),
    convertQuote: builder.mutation({
      query: (data) => ({
        url: BILLING_API.convertQuote,
        method: 'POST',
        body: data,
      }),
    }),

    // ─── DIDs ────────────────────────────────────────────────────────────
    searchDids: builder.mutation({
      query: (filter) => ({
        url: BILLING_API.searchDids,
        method: 'GET',
      }),
    }),
    buyDid: builder.mutation({
      query: (data) => ({
        url: BILLING_API.buyDid,
        method: 'POST',
        body: data,
      }),
    }),

    // ─── Usage & Monitoring ──────────────────────────────────────────────
    getUsage: builder.mutation({
      query: (filter) => ({
        url: BILLING_API.getUsage,
        method: 'POST',
        body: filter,
      }),
    }),
    checkUsageThresholds: builder.mutation({
      query: (data) => ({
        url: BILLING_API.usageThresholds,
        method: 'POST',
        body: data,
      }),
    }),
    runCleanup: builder.mutation({
      query: (data) => ({
        url: BILLING_API.cleanup,
        method: 'POST',
        body: data,
      }),
    }),

    // ─── Moyasar Payment Gateway ─────────────────────────────────────────
    moyasarGenerateToken: builder.mutation({
      query: (data) => ({
        url: BILLING_API.moyasarGenerateToken,
        method: 'POST',
        body: data,
      }),
    }),
    moyasarCreatePayment: builder.mutation({
      query: (data) => ({
        url: BILLING_API.moyasarCreatePayment,
        method: 'POST',
        body: data,
      }),
    }),
    moyasarCreateSubscription: builder.mutation({
      query: (data) => ({
        url: BILLING_API.moyasarCreateSubscription,
        method: 'POST',
        body: data,
      }),
    }),
    moyasarRegisterWebhook: builder.mutation({
      query: (data) => ({
        url: BILLING_API.moyasarRegisterWebhook,
        method: 'POST',
        body: data,
      }),
    }),

    // ─── Admin APIs ──────────────────────────────────────────────────────
    registerOnChargebee: builder.mutation({
      query: (data) => ({
        url: BILLING_API.registerOnChargebee,
        method: 'POST',
        body: data,
      }),
    }),
    quickManualCharge: builder.mutation({
      query: (data) => ({
        url: BILLING_API.manualCharge,
        method: 'POST',
        body: data,
      }),
    }),
    listItemFamilies: builder.mutation({
      query: () => ({
        url: BILLING_API.itemFamilies,
        method: 'GET',
      }),
    }),
    jerasoftProxy: builder.mutation({
      query: ({ method, ...data }) => ({
        url: `${BILLING_API.jerasoftProxy}/${method}`,
        method: 'POST',
        body: data,
      }),
    }),
    // ─── Add / Change Subscription Plan (permissions, plan-change) ────────
    addOrChangeSubscriptionPlan: builder.mutation<
      any,
      { planId: string; packagesId: string; key: string }
    >({
      query: (data) => ({
        url: BILLING_API.planChange,
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

// ─── Exported Hooks ────────────────────────────────────────────────────────
// Existing hooks (preserved for backward compatibility)
export const {
  useGetSubscriptionListMutation,
  useGetPlanListMutation,
  useBuySubscriptionMutation,
  useGetAddonsListMutation,
  useBuyAddonsMutation,
  useInvoiceListMutation,
  useInvoiceDetailMutation,
  useGetLowBalanceMutation,
  useAddBalanceMutation,
  useGetUsageMutation,
  useGetCompareSubscriptionChangeMutation,
  useChangeSubscriptionPlanQuantityMutation,
  useRegisterOnChargebeeMutation,
  useMarkInvoiceAsPaidMutation,
  useGetTransactionListMutation,
  useDiscountListMutation,
  useDiscountAddMutation,
  useDiscountUpdateMutation,
  useDiscountDetailsMutation,
  useDiscountDeleteMutation,
  useEstimateMutation,
  useChargeEstimateMutation,
  useCouponListMutation,
  useProcessPaymentMutation,
  // New hooks
  useCreateSubscriptionMutation,
  useCancelSubscriptionMutation,
  useReactivateSubscriptionMutation,
  useRenewSubscriptionMutation,
  useRenewAddonSubscriptionMutation,
  useExpireSubscriptionsMutation,
  useCreateCheckoutMutation,
  usePortalMutation,
  useGetAddonSubscriptionsMutation,
  useSearchDidsMutation,
  useBuyDidMutation,
  useCheckUsageThresholdsMutation,
  useRunCleanupMutation,
  useCreateQuoteMutation,
  useConvertQuoteMutation,
  useRemoveAllCouponsMutation,
  useMoyasarGenerateTokenMutation,
  useMoyasarCreatePaymentMutation,
  useMoyasarCreateSubscriptionMutation,
  useMoyasarRegisterWebhookMutation,
  useQuickManualChargeMutation,
  useListItemFamiliesMutation,
  useJerasoftProxyMutation,
  useAddOrChangeSubscriptionPlanMutation,
} = billingApi;
